import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, ClipboardList, Loader2, AlertCircle, ImagePlus, X } from 'lucide-react';
import {
  publicIntakeGetByToken,
  publicIntakeSaveDraft,
  uploadIntakeFormPhoto,
  removeIntakeFormPhoto,
  intakePhotoMaxFromOptions,
  normalizeIntakePhotoUrls,
  type IntakeFieldSnapshot,
} from '../lib/crmIntakeFormInstances';

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function IntakeBusinessHeader({ name, logoUrl }: { name: string; logoUrl: string }) {
  const n = name.trim();
  const logo = logoUrl.trim();
  if (!n && !logo) return null;
  return (
    <div className="max-w-lg mx-auto mb-5 flex flex-col items-center text-center gap-2">
      {logo ? (
        <img
          src={logo}
          alt={n ? `${n} logo` : 'Business logo'}
          className="max-h-16 max-w-[220px] w-auto object-contain rounded-lg"
        />
      ) : null}
      {n ? <h2 className="text-lg font-semibold text-slate-800 tracking-tight">{n}</h2> : null}
    </div>
  );
}

function coerceValue(field: IntakeFieldSnapshot, raw: string): string | number | boolean {
  if (field.field_type === 'checkbox') return raw === 'true' || raw === 'on';
  if (field.field_type === 'number') {
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : raw;
  }
  return raw;
}

export default function PublicIntakeForm() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [businessLogoUrl, setBusinessLogoUrl] = useState('');
  const [fields, setFields] = useState<IntakeFieldSnapshot[]>([]);
  const [responses, setResponses] = useState<Record<string, unknown>>({});
  const [locked, setLocked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [uploadingPhotoKey, setUploadingPhotoKey] = useState<string | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const responsesRef = useRef(responses);

  useEffect(() => {
    responsesRef.current = responses;
  }, [responses]);

  const flushDraftSave = useCallback(async () => {
    if (!token || locked) return;
    if (!UUID_RE.test(token)) return;
    try {
      await publicIntakeSaveDraft(token, responsesRef.current, false);
    } catch (e) {
      console.warn('Intake draft save failed', e);
    }
  }, [token, locked]);

  const scheduleDraftSave = useCallback(() => {
    if (!token || locked) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      void flushDraftSave();
    }, 650);
  }, [token, locked, flushDraftSave]);

  useEffect(() => {
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token || !UUID_RE.test(token)) {
        setLoadError('This link is invalid.');
        setLoading(false);
        return;
      }
      setLoading(true);
      setLoadError(null);
      try {
        const res = await publicIntakeGetByToken(token);
        if (cancelled) return;
        if (!res.ok) {
          setLoadError(res.error === 'not_found' ? 'This form was not found or the link has expired.' : 'Unable to load this form.');
          setLoading(false);
          return;
        }
        setTitle(res.title);
        setBusinessName(res.business_name);
        setBusinessLogoUrl(res.business_logo_url);
        setFields(res.fields);
        setResponses({ ...res.responses });
        const done = res.status === 'submitted';
        setLocked(done);
      } catch (e) {
        console.error(e);
        if (!cancelled) setLoadError('Something went wrong. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const updateField = (key: string, value: unknown) => {
    if (locked) return;
    setResponses((prev) => {
      const next = { ...prev, [key]: value };
      responsesRef.current = next;
      return next;
    });
    scheduleDraftSave();
  };

  const handlePhotoFiles = async (field: IntakeFieldSnapshot, fileList: FileList | null) => {
    if (!token || locked || !fileList?.length) return;
    const max = intakePhotoMaxFromOptions(field.options);
    const current = normalizeIntakePhotoUrls(responsesRef.current[field.key]);
    const room = max - current.length;
    if (room <= 0) return;
    const files = Array.from(fileList).slice(0, room);
    setUploadingPhotoKey(field.key);
    setSubmitError(null);
    try {
      const nextUrls = [...current];
      for (const file of files) {
        const url = await uploadIntakeFormPhoto({ token, fieldKey: field.key, file });
        nextUrls.push(url);
      }
      updateField(field.key, nextUrls);
    } catch (err) {
      console.error(err);
      setSubmitError(err instanceof Error ? err.message : 'Photo upload failed.');
    } finally {
      setUploadingPhotoKey(null);
    }
  };

  const removePhotoAt = async (fieldKey: string, url: string) => {
    if (locked) return;
    try {
      await removeIntakeFormPhoto(url);
    } catch (e) {
      console.warn('Remove intake photo failed', e);
    }
    const current = normalizeIntakePhotoUrls(responsesRef.current[fieldKey]);
    updateField(
      fieldKey,
      current.filter((u) => u !== url),
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || locked) return;
    setSubmitting(true);
    setSubmitError(null);
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    const sorted = [...fields].sort((a, b) => {
      const so = (a.section_order ?? 0) - (b.section_order ?? 0);
      if (so !== 0) return so;
      return (a.field_order ?? 0) - (b.field_order ?? 0);
    });
    for (const field of sorted) {
      if (field.field_type !== 'photo') continue;
      const max = intakePhotoMaxFromOptions(field.options);
      const urls = normalizeIntakePhotoUrls(responsesRef.current[field.key]);
      if (field.is_required && urls.length === 0) {
        setSubmitError('Please add at least one photo for each required photo field.');
        setSubmitting(false);
        return;
      }
      if (urls.length > max) {
        setSubmitError(`You can attach at most ${max} photo(s) for “${field.label}”.`);
        setSubmitting(false);
        return;
      }
    }
    try {
      const result = await publicIntakeSaveDraft(token, responsesRef.current, true);
      if (!result.ok) {
        if (result.error === 'missing_required') {
          setSubmitError('Please fill in all required fields.');
        } else if (result.error === 'photo_invalid') {
          setSubmitError('Photo uploads are invalid or exceed the allowed number for a field.');
        } else if (result.error === 'already_submitted') {
          setLocked(true);
        } else {
          setSubmitError('Could not submit. Please try again.');
        }
        return;
      }
      setLocked(true);
    } catch (err) {
      console.error(err);
      setSubmitError('Could not submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const sortedFields = useMemo(() => {
    return [...fields].sort((a, b) => {
      const so = (a.section_order ?? 0) - (b.section_order ?? 0);
      if (so !== 0) return so;
      return (a.field_order ?? 0) - (b.field_order ?? 0);
    });
  }, [fields]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
        <div className="flex flex-col items-center gap-3 text-slate-600">
          <Loader2 className="h-10 w-10 animate-spin text-emerald-600" />
          <p className="text-sm font-medium">Loading form…</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-6 text-center">
          <AlertCircle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
          <p className="text-slate-800 font-medium">{loadError}</p>
        </div>
      </div>
    );
  }

  if (locked) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center p-6 pt-10">
        <IntakeBusinessHeader name={businessName} logoUrl={businessLogoUrl} />
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
          <div className="h-14 w-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-emerald-600" />
          </div>
          <h1 className="text-lg font-semibold text-slate-900 mb-1">Thank you</h1>
          <p className="text-sm text-slate-600">Your responses have been submitted. You can close this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4">
      <IntakeBusinessHeader name={businessName} logoUrl={businessLogoUrl} />
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
              <ClipboardList className="h-5 w-5 text-emerald-600" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg font-semibold text-slate-900 leading-snug">{title || 'Intake form'}</h1>
              <p className="text-xs text-slate-500 mt-0.5">Your answers save as you go.</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-5 space-y-5">
            {sortedFields.map((field, idx) => {
              const val = responses[field.key];
              const prev = idx > 0 ? sortedFields[idx - 1] : null;
              const showSectionHeading = Boolean(
                field.section_id &&
                  field.section_title &&
                  (!prev || prev.section_id !== field.section_id),
              );
              const commonLabel = (
                <label htmlFor={`f-${field.key}`} className="block text-sm font-medium text-slate-800 mb-1.5">
                  {field.label}
                  {field.is_required && <span className="text-red-500 ml-0.5">*</span>}
                </label>
              );

              const sectionHeading =
                showSectionHeading && field.section_title ? (
                  <div
                    className={idx === 0 ? 'pb-2' : 'pt-4 pb-2 border-t border-slate-100'}
                  >
                    <h2 className="text-sm font-semibold text-slate-800">{field.section_title}</h2>
                  </div>
                ) : null;

              if (field.field_type === 'long_text') {
                return (
                  <React.Fragment key={field.key}>
                    {sectionHeading}
                    <div>
                      {commonLabel}
                      <textarea
                        id={`f-${field.key}`}
                        required={field.is_required}
                        disabled={locked}
                        rows={4}
                        className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 disabled:bg-slate-50"
                        value={val != null ? String(val) : ''}
                        onChange={(e) => updateField(field.key, e.target.value)}
                      />
                    </div>
                  </React.Fragment>
                );
              }

              if (field.field_type === 'checkbox') {
                const checked = val === true || val === 'true';
                return (
                  <React.Fragment key={field.key}>
                    {sectionHeading}
                  <div className="flex items-start gap-3">
                    <input
                      id={`f-${field.key}`}
                      type="checkbox"
                      disabled={locked}
                      checked={checked}
                      onChange={(e) => updateField(field.key, e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <label htmlFor={`f-${field.key}`} className="text-sm text-slate-800">
                      {field.label}
                      {field.is_required && <span className="text-red-500 ml-0.5">*</span>}
                    </label>
                  </div>
                  </React.Fragment>
                );
              }

              if (field.field_type === 'photo') {
                const max = intakePhotoMaxFromOptions(field.options);
                const urls = normalizeIntakePhotoUrls(val);
                return (
                  <React.Fragment key={field.key}>
                    {sectionHeading}
                    <div>
                      <div className="block text-sm font-medium text-slate-800 mb-1.5">
                        {field.label}
                        {field.is_required && <span className="text-red-500 ml-0.5">*</span>}
                      </div>
                      {urls.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {urls.map((url) => (
                            <div key={url} className="relative w-[4.5rem] h-[4.5rem] rounded-lg border border-slate-200 overflow-hidden bg-slate-50">
                              <img src={url} alt="" className="w-full h-full object-cover" />
                              {!locked && (
                                <button
                                  type="button"
                                  onClick={() => void removePhotoAt(field.key, url)}
                                  className="absolute top-0.5 right-0.5 p-0.5 rounded-md bg-black/50 text-white hover:bg-black/70"
                                  aria-label="Remove photo"
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {!locked && urls.length < max && (
                        <div className="flex flex-col gap-1.5">
                          <input
                            id={`photo-${field.key}`}
                            type="file"
                            accept="image/*"
                            multiple
                            className="sr-only"
                            disabled={uploadingPhotoKey === field.key}
                            onChange={(e) => {
                              const fl = e.target.files;
                              void handlePhotoFiles(field, fl);
                              e.target.value = '';
                            }}
                          />
                          <label
                            htmlFor={`photo-${field.key}`}
                            className={`inline-flex items-center justify-center gap-2 min-h-[44px] px-3 rounded-xl border border-dashed border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 cursor-pointer ${
                              uploadingPhotoKey === field.key ? 'opacity-50 pointer-events-none' : ''
                            }`}
                          >
                            {uploadingPhotoKey === field.key ? (
                              <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
                            ) : (
                              <ImagePlus className="h-4 w-4 text-emerald-600" />
                            )}
                            {urls.length === 0 ? 'Add photos' : 'Add more'}
                          </label>
                        </div>
                      )}
                      <p className="text-xs text-slate-500 mt-1">
                        {urls.length}/{max} photo{max === 1 ? '' : 's'} — JPEG, PNG, WebP, or HEIC.
                      </p>
                    </div>
                  </React.Fragment>
                );
              }

              if (field.field_type === 'single_select') {
                const opts = Array.isArray(field.options) ? field.options : [];
                return (
                  <React.Fragment key={field.key}>
                    {sectionHeading}
                  <div>
                    {commonLabel}
                    <select
                      id={`f-${field.key}`}
                      required={field.is_required}
                      disabled={locked}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 disabled:bg-slate-50"
                      value={val != null ? String(val) : ''}
                      onChange={(e) => updateField(field.key, e.target.value)}
                    >
                      <option value="">{field.is_required ? 'Select…' : '—'}</option>
                      {opts.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </div>
                  </React.Fragment>
                );
              }

              const inputType =
                field.field_type === 'email'
                  ? 'email'
                  : field.field_type === 'phone'
                    ? 'tel'
                    : field.field_type === 'number'
                      ? 'number'
                      : 'text';

              return (
                <React.Fragment key={field.key}>
                  {sectionHeading}
                <div>
                  {commonLabel}
                  <input
                    id={`f-${field.key}`}
                    type={inputType}
                    required={field.is_required}
                    disabled={locked}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 disabled:bg-slate-50"
                    value={val != null && typeof val !== 'boolean' ? String(val) : ''}
                    onChange={(e) => {
                      const raw = e.target.value;
                      updateField(field.key, coerceValue(field, raw));
                    }}
                  />
                </div>
                </React.Fragment>
              );
            })}

            {submitError && (
              <div className="rounded-xl bg-amber-50 border border-amber-100 px-3 py-2 text-sm text-amber-900">{submitError}</div>
            )}

            <button
              type="submit"
              disabled={submitting || locked}
              className="w-full min-h-[48px] rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 active:scale-[0.99] transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Submitting…' : 'Submit'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
